import { useAuth } from '@/lib/auth'
import { getDeviceLocation } from '@/lib/device-location'
import { captureException } from '@/lib/errors'
import { log } from '@/lib/logger'
import { useMutation } from '@/lib/mutations'
import { offlineExecutor } from '@/lib/offline'
import { newRecordId, useStore } from '@/lib/pocketbase'

interface AdminAddTimeEntryData {
    targetUserId: string
    orgId: string
    job: string
    location?: string
    clock_in: string
    clock_out: string
    duration: number
    notes?: string
    entry_type?: 'manual' | 'break'
}

export function useAdminAddTimeEntry() {
    const { user } = useAuth()
    const [timeEntriesCollection] = useStore('time_entries')

    const mutation = useMutation({
        mutationFn: async (data: AdminAddTimeEntryData) => {
            if (!user?.id) {
                throw new Error('User not found')
            }

            const now = new Date().toISOString()
            const entryId = newRecordId()

            const tx = offlineExecutor.createOfflineTransaction({
                mutationFnName: 'syncTimeEntries',
            })
            tx.mutate(() => {
                timeEntriesCollection.insert({
                    id: entryId,
                    user: data.targetUserId,
                    org: data.orgId,
                    job: data.job,
                    location: data.location || '',
                    clock_in: data.clock_in,
                    clock_out: data.clock_out,
                    duration: data.duration,
                    status: 'approved',
                    entry_type: data.entry_type || 'manual',
                    notes: data.notes || '',
                    approved_by: user.id,
                    approved_at: now,
                    modified_by: user.id,
                    rejection_reason: '',
                    clock_in_location: null,
                    clock_out_location: null,
                })
            })

            getDeviceLocation()
                .then(location => {
                    if (!location) return
                    const locationTx = offlineExecutor.createOfflineTransaction({
                        mutationFnName: 'syncTimeEntries',
                    })
                    locationTx.mutate(() => {
                        timeEntriesCollection.update(entryId, draft => {
                            draft.clock_in_location = location
                        })
                    })
                    log.debug('Admin entry location captured', location)
                })
                .catch(() => {})
        },
        onError: error => {
            captureException('Admin add time entry failed', error)
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}
